import { el } from "../lib/dom.js";
import type { Store, SyncState } from "../data/store.js";
import type { Shell } from "./ui.js";

/**
 * Settings' Sign out, and what it does about changes this device has not sent
 * (g60).
 *
 * `shell.signOut()` stays the unconditional wipe, and has to: after Sign out a
 * shared phone holds nothing of the account, whether or not the server heard.
 * So the question lives here, in front of it. An empty queue goes straight
 * through, exactly as before. A pending one is sent first, for a few seconds,
 * because most of the time it is a second from the server and a question about
 * it would be a false alarm. Only what is still unsent after that is asked
 * about, and "Sign out anyway" still wipes it.
 *
 * An open form and a recovered import are deliberately not counted: Andre
 * settled that the queue and an unsaved write are what the question is for.
 */

/** How long Sign out tries to send before it asks. */
export const SIGN_OUT_WAIT_MS = 5_000;

export const SIGN_OUT = {
  label: "Sign out",
  sending: "Sending changes…",
  title: "Sign out and lose changes?",
  pending: "Some changes haven't reached Trayflux yet. Signing out deletes them from this device.",
  unsaved: "Your last change wasn't saved. Signing out loses it.",
  keep: "Stay signed in",
  act: "Sign out anyway",
};

type Host = Pick<Shell, "store" | "confirm" | "announce" | "signOut">;

/**
 * Asks the store to send, and says whether the queue emptied.
 *
 * Driven by sync-state events, because `store.sync()` cannot be awaited for
 * this: offline it never resolves, it retries until it is stopped. The link
 * state at the moment of the call describes the *last* attempt, so it is not
 * read here: a kicked pass announces itself as `ok` first, and only what it
 * says after that is an answer. `done(true)` when the queue is empty,
 * `done(false)` when the pass says it cannot send, or when the time is up.
 * The returned function cancels without calling `done`.
 */
export function waitToSend(store: Store, ms: number, done: (sent: boolean) => void): () => void {
  let over = false;
  const finish = (sent: boolean) => {
    if (over) return;
    stop();
    done(sent);
  };
  const stopWatching = store.onSyncState((state: SyncState) => {
    if (state.queue === "empty") finish(true);
    else if (state.queue === "unsaved" || state.link !== "ok") finish(false);
  });
  const timer = setTimeout(() => finish(false), ms);
  function stop() {
    over = true;
    stopWatching();
    clearTimeout(timer);
  }
  void store.sync();
  return stop;
}

export function signOutControl(host: Host): { node: HTMLButtonElement; dispose(): void } {
  let phase: "idle" | "sending" | "asking" | "leaving" = "idle";
  let disposed = false;
  let stopWait: (() => void) | undefined;

  // `aria-disabled` rather than `disabled` while it sends, so a keyboard user
  // who pressed it keeps their place on it and hears what it is doing.
  const button = el("button", {
    class: "secondary settings-sign-out",
    text: SIGN_OUT.label,
    attrs: { type: "button" },
    on: { click: press },
  });

  function paint(label: string, busy: boolean) {
    button.textContent = label;
    if (busy) button.setAttribute("aria-disabled", "true");
    else button.removeAttribute("aria-disabled");
  }

  function press() {
    // A second press during the wait, the question or the sign-out itself
    // does nothing; before g60 it started a second wipe.
    if (phase !== "idle") return;
    const state = host.store.syncState();
    if (state.queue === "empty") return leave();
    // Neither of these can change by sending: an unsaved write clears only on
    // a later good write, and an expired session only on signing in again.
    if (state.queue === "unsaved" || state.link === "signed-out") return ask();
    phase = "sending";
    paint(SIGN_OUT.sending, true);
    host.announce(SIGN_OUT.sending);
    stopWait = waitToSend(host.store, SIGN_OUT_WAIT_MS, (sent) => {
      stopWait = undefined;
      if (disposed) return;
      if (sent) leave();
      else ask();
    });
  }

  function ask() {
    const { queue } = host.store.syncState();
    // The timer and the drain can cross: if it went after all, nothing is lost.
    if (queue === "empty") return leave();
    phase = "asking";
    paint(SIGN_OUT.label, false);
    host.confirm(
      {
        title: SIGN_OUT.title,
        message: queue === "unsaved" ? SIGN_OUT.unsaved : SIGN_OUT.pending,
        keep: { label: SIGN_OUT.keep, onChoose: () => { phase = "idle"; } },
        // Red, with the tap guard red brings: offline this question is up
        // within milliseconds of the press, and the second half of a double
        // tap must not be what deletes the changes.
        act: { label: SIGN_OUT.act, danger: true, onChoose: leave },
      },
      { returnFocusTo: () => (button.isConnected ? button : undefined) },
    );
  }

  function leave() {
    phase = "leaving";
    paint(SIGN_OUT.label, true);
    host.signOut();
  }

  return {
    node: button,
    dispose() {
      // Leaving Settings mid-wait is changing your mind: nothing is asked and
      // nobody is signed out behind the page.
      disposed = true;
      stopWait?.();
      stopWait = undefined;
    },
  };
}
